import {
  Box,
  Flex,
  FormControl,
  Heading,
  Input,
  Select,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import AdsetsUsage from "components/planUsage/AdsetsUsage";
import AdsUsage from "components/planUsage/AdsUsage";
import CampaignsUsage from "components/planUsage/CampaignsUsage";
import SidebarWithHeader from "components/SidebarWithHeader";
import moment from "moment";
import { useEffect, useReducer } from "react";
import DatePicker from "react-datepicker";
import {
  // Removed: useImageUsageQuery, useVideoUsageQuery
  useAdsetsUsageQuery,
  useAdsUsageQuery,
  useCampaignsUsageQuery,
} from "store/Subscription/PlanUsageApi";

// URL param setup
const FEATURE_PARAM = "feature";
const VALID_FEATURES = new Set([
  // Hidden for now: "Image", "Video",
  "Campaigns",
  "Adsets",
  "Ads",
  "Ad Rules",
  "Bot Nullifier",
  "Bid Multiplier",
]);

// DatePicker Component
function ChakraDatePicker({ selectedDate, onChange, minDate, maxDate, isDisabled, placeholder }) {
  const selectedAsDate =
    selectedDate instanceof Date
      ? selectedDate
      : typeof selectedDate === "number"
      ? new Date(selectedDate)
      : null;

  return (
    <FormControl display="flex" flexDirection="column" alignItems="flex-start" w="auto">
      <Box>
        <DatePicker
          selected={selectedAsDate}
          onChange={onChange}
          customInput={
            <Input width="120px" h={"30px"} fontSize="sm" size={"xs"} borderRadius={"md"} />
          }
          dateFormat="yyyy-MM-dd"
          minDate={minDate}
          maxDate={maxDate}
          placeholderText={placeholder}
          disabled={isDisabled}
        />
      </Box>
    </FormControl>
  );
}

// Reducer
const initialState = {
  // Default to Campaigns on first load
  selectedOption: "Campaigns",
  startDate: new Date().setDate(new Date().getDate() - 6),
  endDate: new Date(),
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_OPTION":
      return { ...state, selectedOption: action.payload };
    case "SET_START_DATE":
      return { ...state, startDate: action.payload ?? state.startDate };
    case "SET_END_DATE":
      return { ...state, endDate: action.payload ?? state.endDate };
    default:
      return state;
  }
}

// Placeholder components
function AdRulesUsage() {
  return <Box>Ad Rules usage content goes here</Box>;
}
function BotNullifierUsage() {
  return <Box>Bot Nullifier usage content goes here</Box>;
}
function BidMultiplierUsage() {
  return <Box>Bid Multiplier usage content goes here</Box>;
}

// Main Component
function PlanUsage() {
  const textColorSub = useColorModeValue("brand.300", "brand.700");
  const [state, dispatch] = useReducer(reducer, initialState);
  const { selectedOption, startDate, endDate } = state;

  // Sync selected feature with URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(FEATURE_PARAM);

    if (fromUrl && VALID_FEATURES.has(fromUrl)) {
      if (fromUrl !== selectedOption) {
        dispatch({ type: "SET_OPTION", payload: fromUrl });
      }
    } else {
      // If URL has an invalid/hidden feature, enforce current (Campaigns)
      params.set(FEATURE_PARAM, selectedOption);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    }

    const onPopState = () => {
      const p = new URLSearchParams(window.location.search);
      const feature = p.get(FEATURE_PARAM);
      if (feature && VALID_FEATURES.has(feature) && feature !== selectedOption) {
        dispatch({ type: "SET_OPTION", payload: feature });
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionChange = (e) => {
    const value = e.target.value;
    dispatch({ type: "SET_OPTION", payload: value });

    const params = new URLSearchParams(window.location.search);
    params.set(FEATURE_PARAM, value);
    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
  };

  // Kept only the active queries
  const { data: campaignsUsage, isLoading: isCampaignsUsageLoading } = useCampaignsUsageQuery(
    { startDate: moment(startDate).format("YYYY-MM-DD"), endDate: moment(endDate).format("YYYY-MM-DD") },
    { skip: selectedOption !== "Campaigns" }
  );

  const { data: adsetsUsage, isLoading: isAdsetsUsageLoading } = useAdsetsUsageQuery(
    { startDate: moment(startDate).format("YYYY-MM-DD"), endDate: moment(endDate).format("YYYY-MM-DD") },
    { skip: selectedOption !== "Adsets" }
  );

  const { data: adsUsage, isLoading: isAdsUsageLoading } = useAdsUsageQuery(
    { startDate: moment(startDate).format("YYYY-MM-DD"), endDate: moment(endDate).format("YYYY-MM-DD") },
    { skip: selectedOption !== "Ads" }
  );

  const renderContent = () => {
    switch (selectedOption) {
      // Hidden for now:
      // case "Image":
      //   return <ImageUsage data={imageUsage} isLoading={isImagesUsageLoading} />;
      // case "Video":
      //   return <VideoUsage data={videoUsage} isLoading={isVideosUsageLoading} />;
      case "Campaigns":
        return <CampaignsUsage data={campaignsUsage} isLoading={isCampaignsUsageLoading} />;
      case "Adsets":
        return <AdsetsUsage data={adsetsUsage} isLoading={isAdsetsUsageLoading} />;
      case "Ads":
        return <AdsUsage data={adsUsage} isLoading={isAdsUsageLoading} />;
      case "Ad Rules":
        return <AdRulesUsage />;
      case "Bot Nullifier":
        return <BotNullifierUsage />;
      case "Bid Multiplier":
        return <BidMultiplierUsage />;
      default:
        return null;
    }
  };

  return (
    <SidebarWithHeader>
      <Box>
        {/* Header */}
        <Flex direction="column" gap={2} mb={4}>
          <Heading as="h1" size="xl">Dashboard</Heading>
          <Text fontSize="14px" color={textColorSub}>
            Select a feature below to view how much of your plan’s quota has been used for that specific tool or service.
          </Text>
        </Flex>

        {/* Filters */}
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 4, md: 2 }}
          alignItems={{ base: "flex-start", md: "center" }}
          justify="space-between"
          flexWrap="wrap"
        >
          {/* Select Dropdown */}
          <Box w={{ base: "100%", md: "250px" }}>
            <Select
              value={selectedOption}
              onChange={handleOptionChange}
              size="sm"
              borderRadius="md"
            >
              <option disabled>Select an option</option>
              {/* Hidden for now:
              <option value="Image">Image</option>
              <option value="Video">Video</option> */}
              <option value="Campaigns">Campaigns</option>
              <option value="Adsets">Ad sets</option>
              <option value="Ads">Ads</option>
              {/* You can re-enable these later:
              <option value="Ad Rules">Ad Rules</option>
              <option value="Bot Nullifier">Bot Nullifier</option>
              <option value="Bid Multiplier">Bid Multiplier</option> */}
            </Select>
          </Box>

          {/* Date Pickers */}
          <Flex gap={2} alignItems="center" flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <ChakraDatePicker
              selectedDate={startDate}
              onChange={(date) => dispatch({ type: "SET_START_DATE", payload: date })}
              maxDate={endDate instanceof Date ? endDate : new Date(endDate)}
              placeholder="Start Date"
            />
            <ChakraDatePicker
              selectedDate={endDate}
              onChange={(date) => dispatch({ type: "SET_END_DATE", payload: date })}
              minDate={startDate instanceof Date ? startDate : new Date(startDate)}
              maxDate={new Date()}
              placeholder="End Date"
            />
          </Flex>
        </Flex>
      </Box>

      {/* Dynamic Content */}
      <Box mt={6}>{renderContent()}</Box>
    </SidebarWithHeader>
  );
}

export default PlanUsage;
